import { useState, useEffect, useCallback } from 'react';
import { expensesApi, incomeApi, creditCardsApi, loansApi, payoffPlansApi, userApi } from '../services/api';
import { Expense, Income, CreditCard, Loan, PayoffPlan } from '../types';

// Generic hook for API-based data management
function useApiData<T>(
  apiService: {
    getAll: () => Promise<T[]>;
    create: (data: any) => Promise<T>;
    update: (id: string, data: any) => Promise<T>;
    delete: (id: string) => Promise<void>;
  }
) {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await apiService.getAll();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
      console.error('Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiService]);

  const createItem = useCallback(async (itemData: any) => {
    try {
      setError(null);
      const newItem = await apiService.create(itemData);
      setData(prev => [...prev, newItem]);
      return newItem;
    } catch (err: any) {
      setError(err.message || 'Failed to create item');
      throw err;
    }
  }, [apiService]);

  const updateItem = useCallback(async (id: string, itemData: any) => {
    try {
      setError(null);
      const updatedItem = await apiService.update(id, itemData);
      setData(prev => prev.map(item => 
        (item as any).id === id ? updatedItem : item
      ));
      return updatedItem;
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
      throw err;
    }
  }, [apiService]);

  const deleteItem = useCallback(async (id: string) => {
    try {
      setError(null);
      await apiService.delete(id);
      setData(prev => prev.filter(item => (item as any).id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete item');
      throw err;
    }
  }, [apiService]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    create: createItem,
    update: updateItem,
    delete: deleteItem,
  };
}

// Specific hooks for each data type
export function useExpenses() {
  return useApiData<Expense>(expensesApi);
}

export function useIncomes() {
  return useApiData<Income>(incomeApi);
}

export function useCreditCards() {
  return useApiData<CreditCard>(creditCardsApi);
}

export function useLoans() {
  return useApiData<Loan>(loansApi);
}

export function usePayoffPlans() {
  return useApiData<PayoffPlan>(payoffPlansApi);
}

// Hook for user balance
export function useUserBalance() {
  const [balance, setBalance] = useState<number>(0);
  const [balanceAsOf, setBalanceAsOf] = useState<Date | null>(null);
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateBalance = useCallback(async (newBalance: number) => {
    try {
      setIsLoading(true);
      setError(null);
      await userApi.updateBalance(newBalance);
      setBalance(newBalance);
      setBalanceAsOf(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to update balance');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const profile = await userApi.getProfile();
      setBalance(profile.balance || 0);
      setBalanceAsOf(profile.balanceAsOf ? new Date(profile.balanceAsOf) : null);
      setLowBalanceThreshold(profile.lowBalanceThreshold ?? 0);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch balance');
      console.error('Balance fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const updateThreshold = useCallback(async (value: number) => {
    try {
      setError(null);
      await userApi.updateThreshold(value);
      setLowBalanceThreshold(value);
    } catch (err: any) {
      setError(err.message || 'Failed to update threshold');
      throw err;
    }
  }, []);

  return {
    balance,
    balanceAsOf,
    lowBalanceThreshold,
    isLoading,
    error,
    updateBalance,
    updateThreshold,
    refetch: fetchBalance,
  };
}